import { QueryClient } from '@tanstack/angular-query-experimental'

type Mode = 'update' | 'delete' | 'insert'

interface InfiniteQueryCache<TPage> {
  pages: TPage[]
  pageParams: unknown[]
}

/**
 * Keep only the first cached page of one infinite query.
 *
 * Call this immediately before explicitly refetching the query so TanStack
 * Query does not sequentially refetch every page retained by infinite scroll.
 */
export function trimInfiniteQueryToFirstPage<TPage>(queryClient: QueryClient, queryKey: readonly unknown[]): void {
  queryClient.setQueryData<InfiniteQueryCache<TPage>>(queryKey, cached => {
    if (!cached || cached.pages.length <= 1) return cached

    return {
      ...cached,
      pages: cached.pages.slice(0, 1),
      pageParams: cached.pageParams.slice(0, 1),
    }
  })
}

export interface UpdateCacheOptions<T = any> {
  // Key in your entity that identifies it. Defaults to 'id'
  idKey?: keyof T & string
  // How to treat the incoming data
  mode?: Mode
  // Custom matcher if you don't rely on idKey
  match?: (item: T) => boolean
  // Where to insert when mode === 'insert'
  insertPosition?: 'start' | 'end'
}

/**
 * Update list-like cached queries for the given queryKey across all matches.
 *
 * Supports both:
 * - simple arrays: T[]
 * - infinite queries with pages, where a page is either `{ items: T[] }` or `T[]` directly
 *
 * Usage example:
 *   updateCachedListQueries(queryClient, ['categories'], updatedCategory)
 *   updateCachedListQueries(queryClient, ['categories'], deletedCategory, { mode: 'delete' })
 *   updateCachedListQueries(queryClient, ['categories'], newCategory, { mode: 'insert', insertPosition: 'start' })
 */
export function updateCachedListQueries<T extends Record<string, any>>(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  data: T,
  options: UpdateCacheOptions<T> = {}
): void {
  const { idKey = 'id' as keyof T & string, mode = 'update', match, insertPosition = 'end' } = options

  const idVal = data?.[idKey]
  const idStr = idVal != null ? String(idVal) : undefined

  const predicate = (item: T): boolean => {
    if (typeof match === 'function') return match(item)
    if (idStr === undefined) return false
    const v = (item as any)?.[idKey]
    return String(v) === idStr
  }

  const applyToArray = (arr: T[]): T[] => {
    if (!Array.isArray(arr)) return arr
    switch (mode) {
      case 'update':
        return arr.map((t: T) => (predicate(t) ? ({ ...(t as any), ...(data as any) } as T) : t))
      case 'delete':
        return arr.filter((t: T) => !predicate(t))
      case 'insert': {
        // Avoid duplicates if already exists by predicate
        const exists = arr.some(predicate)
        if (exists) return arr
        return insertPosition === 'start' ? [data as T, ...arr] : [...arr, data as T]
      }
      default:
        return arr
    }
  }

  const applyToPage = (page: any): any => {
    // page can be { items: T[], ... } or a raw T[]
    if (Array.isArray(page)) {
      return applyToArray(page)
    }
    if (Array.isArray(page?.items)) {
      return { ...page, items: applyToArray(page.items as T[]) }
    }
    return page
  }

  const matches = queryClient.getQueriesData<any>({ queryKey })
  for (const [key, cached] of matches as Array<[unknown[], any]>) {
    if (!cached) continue

    // Infinite query shape: { pages: any[], pageParams: any[] }
    if (Array.isArray((cached as any)?.pages)) {
      const updated = {
        ...cached,
        pages: (cached as any).pages.map((page: any) => applyToPage(page)),
      }
      queryClient.setQueryData(key as any, updated)
      continue
    }

    // Simple list shape: T[]
    if (Array.isArray(cached)) {
      const updated = applyToArray(cached as T[])
      queryClient.setQueryData(key as any, updated)
      continue
    }

    // Root object with items array: { items: T[], ... }
    if (Array.isArray((cached as any)?.items)) {
      const updated = { ...(cached as any), items: applyToArray((cached as any).items as T[]) }
      queryClient.setQueryData(key as any, updated)
      continue
    }

    // Unknown shape — leave as is
  }
}
